import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSite } from 'selectors/siteSelectors';
import { useAppDispatch } from '../../app/hooks';
import { store } from '../../app/store';
import { IFolder, ILibrary } from '../../features/folders/folderAPI';
import { postFolderAsync } from '../../features/folders/folderSlice';

type LibraryFolderContextType = {
  rootFolderId: number | null;
  getRootFolder: () => IFolder | null;
  getFolderFromPath: (path: string[]) => IFolder | null;
  createFolderPath: (path: string[]) => Promise<IFolder | null>;
  selectedFolderId: number | null;
  selectFolder: (folderId: number) => void;
  unselectFolder: () => void;
};

const LibraryFolderContext = createContext<
  LibraryFolderContextType | undefined
>(undefined);

export const useLibraryFolderContext = () => {
  const ctx = useContext(LibraryFolderContext);
  if (!ctx)
    throw new Error(
      'useLibraryFolderContext must be used within a FolderProvider',
    );
  return ctx;
};

function getFolderFromPath(
  root: IFolder | null,
  path: string[],
): IFolder | null {
  if (!root) return null;
  let current: IFolder = root;
  for (const segment of path) {
    if (!current.subFolders) return null;
    const next = current.subFolders.find(
      (f) => f.folderName?.trim() === segment.trim(),
    );
    if (!next) return null;
    current = next;
  }
  return current;
}

// Helper to get the latest library from the redux store directly
function getLatestLibraryFromStore(): ILibrary | null {
  // Adjust this selector to match your redux state shape
  const state = store.getState();
  const site = state.site;
  if (!site) return null;
  const library = site.library;
  if (!library) return null;
  return library;
}

function getFolderFromLibrary(
  folderId: string | number | null,
  library: ILibrary,
) {
  if (library.allFolders)
    return library.allFolders.find((f: IFolder) =>
      folderId === null
        ? f.parentFolder === null
        : typeof folderId === 'number'
          ? f.id === folderId
          : f.folderName === folderId,
    );
  return null;
}

async function createFolderPath(
  path: string[],
  dispatch: any,
  library: ILibrary | null,
): Promise<IFolder | null> {
  let idx = 0;
  let current: IFolder | null = library?.rootFolder ?? null;
  if (library && current) {
    const found = getFolderFromPath(current, path);
    if (found) {
      // console.log('Using existing folder', path);
      return found;
    } else console.log('Creating folders for', path);
  }
  while (idx < path.length && current) {
    const seg = path[idx].trim();
    let next: IFolder | undefined = current.subFolders?.find(
      (f) => f.folderName?.trim() === seg,
    );
    if (!next) {
      // Folder does not exist, create it
      const folderData = {
        folderName: seg,
        parentFolder: current.id,
      };
      const response = await dispatch(postFolderAsync({ data: folderData }));
      if (response.payload?.response?.status !== 201) {
        console.log(
          `❌ Error creating folder ${seg} for ${path.join('/')} - status ${response.payload?.response?.status}`,
        );
        return null;
      }
      const newId = response.payload.response.data._id;
      // After creation, refresh the rootFolder and find to the new folder
      if (!library || !library.allFolders) return null;
      current = library.allFolders.find((f) => f.id === newId) ?? null;
      // let rootFolder = library?.rootFolder ?? null;
      // const newFolder = library.allFolders.find((f) => {
      //   return f.id === response.payload?.response?.data._id;
      // });
      // let traversed: IFolder | null = rootFolder;
      // for (let i = 0; i <= idx; i++) {
      //   const s = path[i].trim();
      //   traversed =
      //     traversed?.subFolders?.find((f) => f.folderName?.trim() === s) ??
      //     null;
      //   if (!traversed) break;
      // }
      // current = traversed;
      idx++;
    } else {
      current = next;
      idx++;
    }
  }
  return current;
}

export const LibraryFolderProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const { library } = useSite();
  const rootFolderId = library?.rootFolder?.id;

  // Add state for selectedFolderId, default to rootFolder?.id or null
  const [selectedFolderId, selectFolderId] = useState<number | null>(
    rootFolderId ?? null,
  );

  // When rootFolder changes, reset selectedFolderId to rootFolder.id
  useEffect(() => {
    selectFolderId(rootFolderId ?? null);
  }, [rootFolderId]);

  const selectFolder = (folderId: number) => {
    selectFolderId(folderId);
  };

  const unselectFolder = () => {
    selectFolderId(rootFolderId ?? null);
  };

  function getRootFolder(): IFolder | null {
    if (!library) return null;
    return getFolderFromLibrary(null, library) ?? null;
  }

  const value: LibraryFolderContextType = useMemo(
    () => ({
      rootFolderId: rootFolderId ?? null,
      getRootFolder: getRootFolder,
      getFolderFromPath: (path) => getFolderFromPath(getRootFolder(), path),
      createFolderPath: (path) => createFolderPath(path, dispatch, library),
      selectedFolderId,
      selectFolder,
      unselectFolder,
    }),
    [rootFolderId, dispatch, selectedFolderId],
  );

  return (
    <LibraryFolderContext.Provider value={value}>
      {children}
    </LibraryFolderContext.Provider>
  );
};
